import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { Department } from "@enums/department.enum";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { Shift } from "@enums/shift.enum";
import { environment } from "@environments/environment";
import { GetProductionStatusColorOpsPlanner } from "@helpers/getProductionStatusColorOpsPlanner";
import { GetProductionStatusColorOpsPlannerButton } from "@helpers/getProductionStatusColorOpsPlannerButton";
import { GetProductionStatusColorOpsPlannerButtonText } from "@helpers/getProductionStatusColorOpsPlannerButtonText";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ChangeEdiOrderAssignment } from "@interfaces/dto/changeEdiOrderAssignment.interface";
import { ChangeEdiOrderPriority } from "@interfaces/dto/changeEdiOrderPriority.interface";
import { ChangeWorkOrderFirewallAssignmentDto } from "@interfaces/dto/changeWorkOrderFirewallAssignmentDto.interface";
import { ChangeWorkOrderFirewallPriorityDto } from "@interfaces/dto/changeWorkOrderFirewallPriorityDto.interface";
import { Site } from "@interfaces/site.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AllocationService } from "@services/allocation.service";
import { EdiOrderService } from "@services/ediOrder.service";
import { MachineService } from "@services/machine.service";
import { SiteService } from "@services/site.service";
import { TrainingService } from "@services/trainingService.service";
import { UserProfileService } from "@services/userProfile.service";
import { WorkOrderService } from "@services/workOrder.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "firewall",
	styleUrls: ["firewall.css"],
	templateUrl: "firewall.html"
})
export class OpsPlanningFirewallAdminComponent {
	department: Department = Department.Firewall;
	departmentId = 55;
	departments: any;
	dragOverMachineId: number | null = null;
	dragTargetOrderId: number | null = null;
	draggedFromMachineId: number | null = null;
	draggedOrderId: number | null = null;
	draggedOrderType: number | null = null;
	dropdownUserProfiles: UserProfile[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isLoading = false;
	machines: any[] = [];
	machinesShowingAllToday: number[] = [];
	popupAddButtonOptions: any;
	popupChangeEdiOrderAssignment: ChangeEdiOrderAssignment = new ChangeEdiOrderAssignment();
	popupChangeMachineProductionStatus: { machineId: number, productionStatus: ProductionStatus } = { machineId: 0, productionStatus: ProductionStatus.Active };
	popupChangeWorkOrderFirewallAssignmentDto: ChangeWorkOrderFirewallAssignmentDto = new ChangeWorkOrderFirewallAssignmentDto();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupTitle = "Change Machine";
	popupVisible = false;
	priorities: number[];
	productionStatuses: any;
	shifts: any;
	sites: Site[] = [];
	title = "Ops Planning";
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private allocationService: AllocationService, private ediOrderService: EdiOrderService, private machineService: MachineService, private siteService: SiteService, private trainingService: TrainingService, private userProfileService: UserProfileService, private workOrderService: WorkOrderService) {
		this.departments = GetPropertiesFromEnum(Department);
		this.productionStatuses = GetPropertiesFromEnum(ProductionStatus);
		this.shifts = GetPropertiesFromEnum(Shift);
		this.getMachines();
		this.getSites();
		this.getUserProfiles();

		this.priorities = [];
		for (let i = 1; i <= environment.build.lowestEdiOrderPriority; i++) {
			this.priorities.push(i);
		}
	}

	assignEdiOrderMachine() {
		this.ediOrderService.assignMachine(this.popupChangeEdiOrderAssignment)
			.subscribe(
				() => {
					this.toastService.success("Edi Order Machine Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	assignEdiOrderMachinePopup(ediOrderId: number) {
		this.popupChangeEdiOrderAssignment = new ChangeEdiOrderAssignment();
		this.popupChangeEdiOrderAssignment.ediOrderId = ediOrderId;
		this.popupAddButtonOptions = { onClick: () => this.assignEdiOrderMachine(), text: "Assign Machine" };
		this.popupTitle = "Assign Machine to Edi Order";
		this.popupVisible = true;
		return false;
	}

	assignEdiOrderOperator() {
		this.ediOrderService.assignOperator(this.popupChangeEdiOrderAssignment)
			.subscribe(
				() => {
					this.toastService.success("Edi Order Operator Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	assignEdiOrderOperatorPopup(ediOrderId: number, machineId: number) {
		if (machineId === undefined || machineId <= 0) {
			alert("Please assign this stage to a machine first.");
			return false;
		}
		
		this.popupChangeEdiOrderAssignment = new ChangeEdiOrderAssignment();
		this.popupChangeEdiOrderAssignment.ediOrderId = ediOrderId;
		this.popupAddButtonOptions = { onClick: () => this.assignEdiOrderOperator(), text: "Assign Operator" };
		this.popupTitle = "Assign Operator to Edi Order";
		this.popupVisible = true;

		return false;
	}

	assignWorkOrderMachine() {
		this.workOrderService.assignFirewallMachine(this.popupChangeWorkOrderFirewallAssignmentDto)
			.subscribe(
				() => {
					this.toastService.success("Work Order Machine Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	assignWorkOrderMachinePopup(workOrderId: number) {		
		this.popupChangeWorkOrderFirewallAssignmentDto = new ChangeWorkOrderFirewallAssignmentDto();
		this.popupChangeWorkOrderFirewallAssignmentDto.workOrderId = workOrderId;
		this.popupAddButtonOptions = { onClick: () => this.assignWorkOrderMachine(), text: "Assign Machine" };
		this.popupTitle = "Assign Machine to Work Order";
		this.popupVisible = true;

		return false;
	}

	assignWorkOrderOperator() {
		this.workOrderService.assignFirewallOperator(this.popupChangeWorkOrderFirewallAssignmentDto)
			.subscribe(
				() => {
					this.toastService.success("Work Order Operator Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	assignWorkOrderOperatorPopup(workOrderId: number, machineId: number) {
		if (machineId === undefined || machineId <= 0) {
			alert("Please assign this stage to a machine first.");
			return false;
		}
		
		this.popupChangeWorkOrderFirewallAssignmentDto = new ChangeWorkOrderFirewallAssignmentDto();
		this.popupChangeWorkOrderFirewallAssignmentDto.workOrderId = workOrderId;
		this.popupAddButtonOptions = { onClick: () => this.assignWorkOrderOperator(), text: "Assign Operator" };
		this.popupTitle = "Assign Operator to Work Order";
		this.popupVisible = true;

		return false;
	}

	buildButtons() {
		this.headerTertiaryButtons = [
			{ method: "", style: "background-color: #10B981; color: white;", text: "Firewall" },
			{ method: "goToManForMan", text: "Operator Plan" },
			{ method: "goToWorkforce", text: "Workforce" },
			{ method: "goToOutsideProcessMovements", text: "OPM" }
		];

		this.headerSecondaryButtons = [];

		const filteredDepartments = this.departments.filter((d: { id: number; name: string; }) => (d.id > 0 && d.id < 55) || d.id == 70);
		filteredDepartments.forEach((fd: any) => {
			this.headerSecondaryButtons.push({ method: "goToDepartment-" + fd.id, text: fd.name });
		});

		this.headerPrimaryButtons = [];

		this.sites.forEach((site: any) => {
			this.headerPrimaryButtons.push({ method: "goToSite-" + site.id, text: site.name });
		});

		const selectedDepartment = this.departments.filter((d: { id: number; name: string; }) => d.id == this.departmentId);
		this.title = selectedDepartment[0].name;
	}

	changeEdiOrderPriority(ediOrderId: number, priority: number) {
		const data = new ChangeEdiOrderPriority();
		data.priority = priority;
		data.ediOrderId = ediOrderId;
		this.ediOrderService.changePriority(data)
			.subscribe(
				() => {
					this.toastService.success("Priority Changed - Refresh page to update details and order.");
					// this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	changeEdiOrderPriorityDropDown(args: any, ediOrderId: number) {
		const newPriority = args.value;

		this.changeEdiOrderPriority(ediOrderId, newPriority);
	}

	changeMachineProductionStatus() {
		this.machineService.updateProductionStatusSupervisor(this.popupChangeMachineProductionStatus.machineId, this.popupChangeMachineProductionStatus.productionStatus)
			.subscribe(
				() => {
					this.toastService.success("Machine Production Status Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeMachineProductionStatusPopup(machineId: number) {
		const selectedMachine = this.machines.filter((m: any) => m.machineId === machineId);
		this.popupChangeMachineProductionStatus.machineId = machineId;
		this.popupChangeMachineProductionStatus.productionStatus = selectedMachine[0].productionStatus;
		this.popupAddButtonOptions = { onClick: () => this.changeMachineProductionStatus(), text: "Change Production Status" };
		this.popupTitle = "Change Production Status";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderPriority(workOrderId: number, priority: number) {
		const data = new ChangeWorkOrderFirewallPriorityDto();
		data.priority = priority;
		data.workOrderId = workOrderId;
		this.workOrderService.changeFirewallPriority(data)
			.subscribe(
				() => {
					this.toastService.success("Priority Changed - Refresh page to update details and order.");
					// this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	changeWorkOrderPriorityDropDown(args: any, workOrderId: number) {
		const newPriority = args.value;

		this.changeWorkOrderPriority(workOrderId, newPriority);
	}

	closePopup() {
		this.popupVisible = false;
	}

	createAllocationForEdiOrder(id: number) {
		if (confirm("Create allocation for this Edi Order? This can be assigned work orders later.") === false) {
			return false;
		}

		// Do the actual creation here		
		this.allocationService.createForEdiOrder(id)
			.subscribe(
				() => {
					this.toastService.success("Allocation Created");
					this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	displayMachineInDropdown(e: any) { return e === null ? null : e.cellNumber === null ? e.machineName : `${e.cellNumber} - ${e.machineName}`; }

	getMachines() {
		this.isLoading = true;
		this.machineService.opsPlannerFirewallOrders()
			.subscribe(
				(res: any) => {
					this.machines = res.response;
					this.isLoading = false;
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
					this.buildButtons();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getUserProfiles() {
		this.userProfileService.byCurrentDepartment(this.department)
			.subscribe(
				(res: any) => {
					this.userProfiles = res.response;
					this.dropdownUserProfiles = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	goToAllocation(id: number) {
		this.router.navigate([`/admin/allocations/${id}`]);
		return false;
	}

	goToDepartment(department: number) {
		this.router.navigate([`/admin/opsPlanning/department/${department}`]);
		return false;
	}

	goToFirewall() {
		this.router.navigate(["/admin/opsPlanning/firewall"]);
		return false;
	}

	goToManForMan() {
		this.router.navigate(["/admin/opsPlanning/operatorPlan"]);
	}

	goToOutsideProcessMovements() {
		this.router.navigate(["/admin/opsPlanning/outsideProcessContractMovements"]);
	}

	goToSite(site: number) {
		this.router.navigate([`/admin/opsPlanning/site/${site}`]);
		return false;
	}

	goToWorkforce() {
		this.router.navigate(["/admin/opsPlanning/workforce"]);
		return false;
	}

	headerButtonClick(method: string) {
		if (method.indexOf("-") > 0) {
			const split = method.split("-");
			// @ts-ignore // Required to be able to call the method directly from the variable
			this[split[0]](split[1]);
		}
		else {
			// @ts-ignore // Required to be able to call the method directly from the variable
			if (this[method]) this[method]();
		}
	}

	onDragEnd() {
		this.draggedOrderId = null;
		this.draggedOrderType = null;
		this.draggedFromMachineId = null;
		this.dragOverMachineId = null;
		this.dragTargetOrderId = null;
	}

	onDragLeave(machineId: number) {
		if (this.dragOverMachineId === machineId) {
			this.dragOverMachineId = null;
		}
	}

	onDragLeaveOrder(orderId: number) {
		if (this.dragTargetOrderId === orderId) {
			this.dragTargetOrderId = null;
		}
	}

	onDragOver(event: DragEvent, machineId: number) {
		event.preventDefault();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}
		this.dragOverMachineId = machineId;
	}

	onDragOverOrder(event: DragEvent, orderId: number, machineId: number) {
		event.preventDefault();
		event.stopPropagation();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}
		this.dragOverMachineId = machineId;
		if (this.draggedFromMachineId === machineId && orderId !== this.draggedOrderId) {
			this.dragTargetOrderId = orderId;
		}
	}

	onDragStart(event: DragEvent, orderId: number, orderType: number, machineId: number) {
		this.draggedOrderId = orderId;
		this.draggedOrderType = orderType;
		this.draggedFromMachineId = machineId;
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = "move";
		}
	}

	onDropOnMachine(targetMachineId: number) {
		if (this.draggedOrderId === null || this.draggedFromMachineId === targetMachineId) {
			this.onDragEnd();
			return;
		}
		this.isLoading = true;
		if (this.draggedOrderType === 0) {
			const data = new ChangeEdiOrderAssignment();
			data.ediOrderId = this.draggedOrderId;
			data.assignedId = targetMachineId;
			this.ediOrderService.assignMachine(data).subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Edi Order moved to new machine");
					this.getMachines();
					this.onDragEnd();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					this.onDragEnd();
				}
			);
		} else {
			const data = new ChangeWorkOrderFirewallAssignmentDto();
			data.workOrderId = this.draggedOrderId;
			data.assignedId = targetMachineId;
			this.workOrderService.assignFirewallMachine(data).subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order moved to new machine");
					this.getMachines();
					this.onDragEnd();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					this.onDragEnd();
				}
			);
		}
	}

	onDropOnOrder(event: DragEvent, targetOrderId: number, targetMachineId: number) {
		event.stopPropagation();
		if (this.draggedOrderId === null || this.draggedOrderId === targetOrderId) {
			this.onDragEnd();
			return;
		}
		if (this.draggedFromMachineId === targetMachineId) {
			this.reorderWithinMachine(targetMachineId, this.draggedOrderId, targetOrderId);
		} else {
			this.onDropOnMachine(targetMachineId);
		}
	}

	renderDateString(date: Date)
	{
		const dateToChange = new Date(date);
		return dateToChange.toLocaleDateString("en-GB");
	}

	renderStatusBackgroundColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlanner(status);
	}

	renderStatusButtonColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlannerButton(status);
	}

	renderStatusButtonTextColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlannerButtonText(status);
	}

	renderStatusColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlannerButtonText(status);
	}

	reorderWithinMachine(machineId: number, draggedId: number, targetId: number) {
		const machine = this.machines.find((m: any) => m.machineId === machineId);
		if (!machine) { this.onDragEnd(); return; }
		const orders = [...machine.firewallOrders].sort((a: any, b: any) => a.priority - b.priority);
		const fromIndex = orders.findIndex((o: any) => o.id === draggedId);
		const toIndex = orders.findIndex((o: any) => o.id === targetId);
		if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) { this.onDragEnd(); return; }
		const [moved] = orders.splice(fromIndex, 1);
		orders.splice(toIndex, 0, moved);
		const updates = orders
			.map((order: any, index: number) => ({ newPriority: index + 1, order }))
			.filter(({ newPriority, order }: { newPriority: number, order: any }) => order.priority !== newPriority)
			.map(({ newPriority, order }: { newPriority: number, order: any }) => {
				if (order.orderType === 0) {
					const data = new ChangeEdiOrderPriority();
					data.ediOrderId = order.id;
					data.priority = newPriority;
					return this.ediOrderService.changePriority(data);
				} else {
					const data = new ChangeWorkOrderFirewallPriorityDto();
					data.workOrderId = order.id;
					data.priority = newPriority;
					return this.workOrderService.changeFirewallPriority(data);
				}
			});
		if (updates.length === 0) { this.onDragEnd(); return; }
		this.isLoading = true;
		forkJoin(updates).subscribe(
			() => {
				this.isLoading = false;
				this.toastService.success("Queue order updated");
				this.getMachines();
				this.onDragEnd();
			},
			(err: any) => {
				this.isLoading = false;
				console.log(err);
				this.toastService.error("Something Went Wrong!");
				this.onDragEnd();
			}
		);
	}

	toggleMachineShowingAllToday(machineId: number) {
		const currentIndex = this.machinesShowingAllToday.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllToday.splice(currentIndex);
		} else {
			this.machinesShowingAllToday.push(machineId);
		}
		return false;
	}
}